/**
 * AlertsHistoryPage — cross-tenant paginated alert history with an
 * isSimulated filter (Session 2026-04-18 Q1).
 *
 * Visual layout mirrors the media library list page: sticky filter card
 * on the left, table + description on the right. Filter shapes here
 * (text, single-select, datetime) don't fit FilterSidebar's multi-select
 * checkbox model, so we hand-roll the card to keep the same look.
 *
 * 014-alerts / US7 / T135.
 */
import { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAlertsHistory } from '../hooks/useAdminAlerts';
import type { AdminAlertHistoryFilter } from '../api/adminAlertsApi';
import { AlertHistoryTable } from '../components/AlertHistoryTable';
import { RegionPicker } from '../components/RegionPicker';

type SimulationFilter = 'all' | 'only-sims' | 'no-sims';

export function AlertsHistoryPage() {
  const { t } = useTranslation();
  const [regionId, setRegionId] = useState('');
  const [simFilter, setSimFilter] = useState<SimulationFilter>('all');
  const [fromUtc, setFromUtc] = useState('');
  const [toUtc, setToUtc] = useState('');
  const [page, setPage] = useState(1);

  const isSimulated =
    simFilter === 'only-sims' ? true : simFilter === 'no-sims' ? false : undefined;

  const filter: AdminAlertHistoryFilter = {
    regionId: regionId || undefined,
    isSimulated,
    fromUtc: fromUtc ? new Date(fromUtc).toISOString() : undefined,
    toUtc: toUtc ? new Date(toUtc).toISOString() : undefined,
    page,
    pageSize: 50,
  };

  const { data, isLoading } = useAlertsHistory(filter);

  const activeFilterCount =
    (regionId ? 1 : 0) + (simFilter !== 'all' ? 1 : 0) + (fromUtc ? 1 : 0) + (toUtc ? 1 : 0);

  const clearAll = () => {
    setRegionId('');
    setSimFilter('all');
    setFromUtc('');
    setToUtc('');
    setPage(1);
  };

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} data-testid="admin-alerts-history">
      <Flex gap={4} align="flex-start">
        {/* Left: sticky filter card — same visual style as media library's FilterSidebar */}
        <Card
          w="280px"
          minW="280px"
          borderRadius="20px"
          boxShadow="0px 18px 40px rgba(112, 144, 176, 0.12)"
          alignSelf="flex-start"
          position="sticky"
          top="24px"
          data-testid="alert-history-filter-sidebar"
        >
          <CardBody px={4} py={5}>
            <Flex justify="space-between" align="center" mb={4}>
              <Flex align="center" gap={2}>
                <Text fontSize="lg" fontWeight="700" color="gray.700">
                  {t('admin.alerts.history.filtersTitle', 'Filters')}
                </Text>
                {activeFilterCount > 0 && (
                  <Badge
                    colorScheme="brand"
                    borderRadius="full"
                    px={2.5}
                    py={0.5}
                    fontSize="xs"
                    fontWeight="600"
                    cursor="pointer"
                    onClick={clearAll}
                    data-testid="alert-history-filter-clear"
                  >
                    {activeFilterCount} ×
                  </Badge>
                )}
              </Flex>
              {activeFilterCount > 0 && (
                <Button
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  onClick={clearAll}
                >
                  {t('common.clear', 'Clear')}
                </Button>
              )}
            </Flex>

            <Stack spacing={4}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  {t('admin.alerts.history.regionFilter', 'Region')}
                </FormLabel>
                <RegionPicker
                  value={regionId}
                  onChange={(id) => {
                    setPage(1);
                    setRegionId(id);
                  }}
                  data-testid="alert-history-filter-region"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  {t('admin.alerts.history.kindFilter', 'Event kind')}
                </FormLabel>
                <Select
                  size="sm"
                  value={simFilter}
                  onChange={(e) => {
                    setPage(1);
                    setSimFilter(e.target.value as SimulationFilter);
                  }}
                  borderRadius="10px"
                  data-testid="alert-history-filter-kind"
                >
                  <option value="all">{t('admin.alerts.history.kindAll', 'All')}</option>
                  <option value="no-sims">
                    {t('admin.alerts.history.kindRealOnly', 'Real alerts only')}
                  </option>
                  <option value="only-sims">
                    {t('admin.alerts.history.kindSimOnly', 'Simulations only')}
                  </option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  {t('admin.alerts.history.fromFilter', 'From')}
                </FormLabel>
                <Input
                  type="datetime-local"
                  size="sm"
                  value={fromUtc}
                  onChange={(e) => {
                    setPage(1);
                    setFromUtc(e.target.value);
                  }}
                  borderRadius="10px"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  {t('admin.alerts.history.toFilter', 'To')}
                </FormLabel>
                <Input
                  type="datetime-local"
                  size="sm"
                  value={toUtc}
                  onChange={(e) => {
                    setPage(1);
                    setToUtc(e.target.value);
                  }}
                  borderRadius="10px"
                />
              </FormControl>
            </Stack>
          </CardBody>
        </Card>

        {/* Right: description + table */}
        <Flex direction="column" flex={1} minW={0}>
          <Text color="gray.600" mb={4}>
            {t(
              'admin.alerts.history.description',
              'Cross-tenant history of every alert event — real and simulated. Use filters to narrow by region, timeframe, or event kind.',
            )}
          </Text>

          <Card borderRadius="20px" boxShadow="0px 18px 40px rgba(112, 144, 176, 0.12)">
            <CardBody>
              <AlertHistoryTable
                page={data}
                isLoading={isLoading}
                onPageChange={(p) => setPage(Math.max(1, p))}
              />
            </CardBody>
          </Card>
        </Flex>
      </Flex>
    </Box>
  );
}
